/**
 * /held — operators: every analysis held before publishing, in every
 * workspace (GET /ops/held), with the reasons and the pipeline's warnings,
 * and the two decisions. The clips of a held run are on its video page, for
 * a member of its workspace. Not under /ops: that prefix is the API's.
 */
import { useState } from "react";
import { Link } from "react-router";
import { get, post } from "../api/client";
import { useLoad } from "../api/hooks";
import type { RunWarning } from "../api/types";
import { Shell } from "../components/Shell";
import { ErrorLine } from "../components/ui";

interface HeldItem {
  video_id: string;
  workspace_id: string;
  title: string;
  run_id: string;
  revision: number;
  reasons: { code: string; message: string }[];
  warnings: RunWarning[];
  held_at: string;
}

export default function HeldPage() {
  const list = useLoad(() => get<{ items: HeldItem[] }>("/ops/held"), []);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState<Error | null>(null);
  const act = async (item: HeldItem, what: "release" | "reanalyze") => {
    const q = what === "release" ? `Publish "${item.title}" as it is? The user is emailed that the clips are ready.` : `Run the analysis of "${item.title}" again?`;
    if (!window.confirm(q)) return;
    setBusy(item.video_id + what);
    setError(null);
    try {
      await post(`/ops/videos/${item.video_id}/${what}`, {});
      await list.reload();
    } catch (e) {
      setError(e as Error);
    } finally {
      setBusy("");
    }
  };
  const items = list.data?.items ?? [];
  return (
    <Shell back="/">
      <div className="p" style={{ paddingTop: 24, paddingBottom: 40 }} data-testid="held-page">
        <h1>Held for review</h1>
        <p className="sub mt10">Analyses the review stopped before publishing. Operators only.</p>
        <ErrorLine error={list.error ?? error} />
        {list.data && items.length === 0 && <p className="tiny mt24">Nothing is held.</p>}
        {items.map((it) => (
          <section key={it.run_id} className="review mt16" data-decision="hold">
            <h3><Link className="u" to={`/videos/${it.video_id}`}>{it.title}</Link></h3>
            <p className="tiny mt6">Run {it.revision} · held {new Date(it.held_at).toLocaleString()} · workspace {it.workspace_id}</p>
            <ul className="mt10">{it.reasons.map((x) => <li key={x.code + x.message}><b>{x.code}</b> {x.message}</li>)}</ul>
            {it.warnings.length > 0 && (
              <details className="mt10">
                <summary>{it.warnings.length} pipeline warning{it.warnings.length === 1 ? "" : "s"}</summary>
                <ul className="mt6">{it.warnings.map((w) => <li key={w.source + w.message} className="tiny">[{w.source}] {w.message}{w.count > 1 ? ` (×${w.count})` : ""}{w.detail ? <><br /><code>{w.detail}</code></> : null}</li>)}</ul>
              </details>
            )}
            <div className="row mt10" style={{ gap: 8 }}>
              <button className="btn dark" disabled={busy !== ""} onClick={() => act(it, "release")}>{busy === it.video_id + "release" ? "Publishing…" : "Publish as it is"}</button>
              <button className="btn ghost" disabled={busy !== ""} onClick={() => act(it, "reanalyze")}>{busy === it.video_id + "reanalyze" ? "Queuing…" : "Run the analysis again"}</button>
            </div>
          </section>
        ))}
      </div>
    </Shell>
  );
}
